import { useMemo } from "react";
import { Chart } from "chart.js";
import { find, map, orderBy, reject, size, slice, sumBy, take } from "lodash";
import { Item as Serie } from "@charty/legend/item/model";
import { content, darken, lighten } from "@glossy";
import { GlossyTheme } from "@glossy/theme/types";
import { Locale } from "@linkfluence/models/locale";
import { natural } from "@tootools/sort";
import { tr } from "@radarly/i18n";

export const useOtherSeriesIds = (series: Serie[]) =>
  useMemo(() => {
    const orderedSeries = orderBy(series, "count", "desc");
    const otherSeries = slice(orderedSeries, 20);
    return map(otherSeries, "id");
  }, [series]);

export const useSeries = (
  series: Serie[],
  locale: Locale,
  theme: GlossyTheme
) =>
  useMemo(() => {
    if (series.length > 21) {
      const orderedSeries = orderBy(series, "count", "desc");
      const mainSeries = take(orderedSeries, 20);
      const otherSeries = slice(orderedSeries, 20);
      const otherTotal = sumBy(otherSeries, "count");
      return [
        ...mainSeries,
        {
          id: "other",
          label: tr(locale, "CHARTY::OTHER_SERIE"),
          count: otherTotal,
          color: content({ theme }, "secondary"),
        },
      ];
    } else {
      return series;
    }
  }, [series, locale, theme]);

export const useLegendSeries = (series: Serie[]) =>
  useMemo(
    () =>
      map(series, (serie: Serie) => ({
        id: serie.id,
        label: serie.label,
        count: serie.count,
        color: serie.color,
        percentage: 25,
      })),
    [series]
  );

export const useDatasetTotal = (total: number, series: Serie[]) => {
  return useMemo(() => {
    if (total) {
      return total;
    } else {
      return sumBy(series, "count");
    }
  }, [total, series]);
};

export const useSortSeries = (series: Serie[], orderSerieBy: string) =>
  useMemo(() => {
    const otherSerie = find(series, { id: "other" });
    const _series = reject(series, ["id", "other"]);
    const orderedSeries =
      orderSerieBy === "natural"
        ? natural(_series, "label")
        : orderBy(_series, ["count"], ["desc"]);
    return otherSerie ? [...orderedSeries, otherSerie] : orderedSeries;
  }, [series, orderSerieBy]);

export const useBuildData = (sortedSeries: Serie[], theme: GlossyTheme) =>
  useMemo(() => {
    const data = map(sortedSeries, "count");
    const primaryColor = content({ theme }, "primary");
    const backgroundColor = map(
      sortedSeries,
      (serie, cpt) => serie.color || lighten(0.04 * cpt, primaryColor)
    );
    const hoverBackgroundColor = map(backgroundColor, (color) =>
      darken(0.1, color || primaryColor)
    );
    const labels = map(sortedSeries, "label");
    return {
      labels,
      datasets: [{ data, backgroundColor, hoverBackgroundColor }],
    };
  }, [sortedSeries, theme]);

export const updateOnLegendHover = (
  doughnutChart: Chart,
  hoveredId: string,
  ids: (string | number)[],
  colors: string[],
  theme: GlossyTheme
) => {
  const dataset = doughnutChart.data.datasets[0];
  const accentFadedColor = content({ theme }, "tertiary");
  const dataSize = size(dataset.data);
  for (let i = 0; i < dataSize; i++) {
    if (hoveredId && ids[i] !== hoveredId) {
      dataset.backgroundColor[i] = accentFadedColor;
    } else {
      dataset.backgroundColor[i] = colors[i];
    }
  }
  doughnutChart.update();
};
